import { TRAIL_SOURCE, SMOOTH_MOVE_FRAMES, SMOOTH_MOVE_INTERVAL_MS } from '../utils/map-constants.js'

export class TrailRenderer {
  constructor() {
    this.map = null
    this.followedMonitorId = null
    this.followedEntityType = null  // MARKERS-DESIGN-SPEC §6: estilo por tipo
    this.trailCoords = []
    this.animationCounters = new Map()
  }

  init(map) { this.map = map }

  follow(monitorId, currentCoords, entityType) {
    this.followedMonitorId = monitorId
    this.followedEntityType = entityType || 'device'
    this.trailCoords = [currentCoords]
    this._updateTrailSource()
  }

  unfollow() {
    this.followedMonitorId = null
    this.followedEntityType = null
    this.trailCoords = []
    this._updateTrailSource()
  }

  extendTrail(newCoords) {
    if (!this.followedMonitorId) return
    this.trailCoords.push(newCoords)
    if (this.trailCoords.length > 500) this.trailCoords = this.trailCoords.slice(-500)
    this._updateTrailSource()
  }

  loadHistory(positions) {
    this.trailCoords = positions.map(function(p) { return [p.longitude, p.latitude] })
    this._updateTrailSource()
  }

  smoothMove(monitorId, oldCoords, newCoords, onFrame) {
    var counter = (this.animationCounters.get(monitorId) || 0) + 1
    this.animationCounters.set(monitorId, counter)

    var oldLng = oldCoords[0], oldLat = oldCoords[1]
    var dLng = (newCoords[0] - oldLng) / SMOOTH_MOVE_FRAMES
    var dLat = (newCoords[1] - oldLat) / SMOOTH_MOVE_FRAMES
    var self = this

    return new Promise(function(resolve) {
      var frame = 0
      function step() {
        if (self.animationCounters.get(monitorId) !== counter) { resolve(); return }
        frame++
        var lng = oldLng + dLng * frame
        var lat = oldLat + dLat * frame
        onFrame([lng, lat])
        if (monitorId === self.followedMonitorId && frame === SMOOTH_MOVE_FRAMES) {
          self.extendTrail([lng, lat])
        }
        if (frame < SMOOTH_MOVE_FRAMES) {
          setTimeout(step, SMOOTH_MOVE_INTERVAL_MS)
        } else {
          resolve()
        }
      }
      step()
    })
  }

  // Color del monitor que se está siguiendo, para que su rastro se pinte del
  // mismo color que su marker: al seguir una ambulancia el trazo es naranja y
  // al seguir una patrulla azul, en vez del cyan único de antes. Con dos
  // seguimientos a la vez el cyan no dejaba saber cuál era cuál.
  //
  // Se lee del cache global de monitors en lugar de recibirlo por parámetro
  // porque no todos los puntos de arranque pasan por follow(): algunos setean
  // followedMonitorId directamente (ver MapV2, arranque del trail SOS).
  // Devuelve '' si no resuelve, y entonces el paint usa su color por omisión.
  _resolveTrailColor() {
    if (!this.followedMonitorId) return ''
    var monitors = typeof window !== 'undefined' ? window.__mapV2Monitors : null
    var mon = monitors ? monitors[this.followedMonitorId] : null
    var color = mon && mon.color
    // Solo hex/rgb reales: un '' o un 'var(--kln-x)' sin resolver haría
    // fallar el parseo de color de MapLibre y tumbaría la capa entera.
    if (!color || String(color).indexOf('var(') === 0) return ''
    return String(color)
  }

  _updateTrailSource() {
    if (!this.map) return
    var source = this.map.getSource(TRAIL_SOURCE)
    if (!source) return
    // entityType en las properties dispara el filter en las layers de
    // trail — una layer distinta por tipo (device/sos_call/reportant) con
    // paint dedicado. Ver MARKERS-DESIGN-SPEC §6 + layer-manager.addTrailLayers.
    var entityType = this.followedEntityType || 'device'
    // trailColor solo se incluye si resolvió: una propiedad ausente deja que
    // el `coalesce` del paint caiga al color por omisión del tipo, mientras
    // que una propiedad presente pero vacía rompería el parseo de MapLibre.
    var trailColor = this._resolveTrailColor()
    var lineProps = { entityType: entityType }
    if (trailColor) lineProps.trailColor = trailColor
    var features = []
    if (this.trailCoords.length >= 2) {
      features.push({
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: this.trailCoords },
        properties: lineProps,
      })
    }
    // `edad` va de 0 (el punto más viejo del rastro) a 1 (el más reciente).
    // Las layers la usan para que el trazo se vea avanzar: los puntos crecen
    // y se opacan hacia donde va la persona. Sin esto, un rastro es una nube
    // de puntos idénticos en la que no se sabe ni dónde está ahora ni en qué
    // dirección se movió.
    var ultimo = this.trailCoords.length - 1
    for (var i = 0; i <= ultimo; i++) {
      var pointProps = {
        entityType: entityType,
        edad: ultimo > 0 ? i / ultimo : 1,
        actual: i === ultimo,
      }
      if (trailColor) pointProps.trailColor = trailColor
      features.push({
        type: 'Feature',
        geometry: { type: 'Point', coordinates: this.trailCoords[i] },
        properties: pointProps,
      })
    }
    source.setData({ type: 'FeatureCollection', features: features })
  }
}
